// Tiny typed API client. Curator password is stored in localStorage; sent as a
// header on every curator request.

const PASSWORD_KEY = 'egyptle.curator.password'

export function getCuratorPassword(): string | null {
  return localStorage.getItem(PASSWORD_KEY)
}

export function setCuratorPassword(pw: string | null) {
  if (pw === null) localStorage.removeItem(PASSWORD_KEY)
  else localStorage.setItem(PASSWORD_KEY, pw)
}

async function curatorFetch(path: string, init: RequestInit = {}) {
  const pw = getCuratorPassword()
  const headers = new Headers(init.headers)
  if (pw) headers.set('x-curator-password', pw)
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json')
  }
  const res = await fetch(`/api/curator${path}`, { ...init, headers })
  if (res.status === 401) {
    setCuratorPassword(null)
    throw new Error('Bad password — try again')
  }
  return res
}

export const curatorApi = {
  async me() {
    const res = await curatorFetch('/me')
    if (!res.ok) throw new Error(`${res.status}`)
    return res.json()
  },

  async ingest(url: string) {
    const res = await curatorFetch('/ingest', {
      method: 'POST',
      body: JSON.stringify({ url }),
    })
    if (res.status === 409) {
      const data = await res.json()
      const err = new Error('duplicate') as Error & { existing?: unknown }
      err.existing = (data as { existing?: unknown }).existing
      throw err
    }
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error((data as { error?: string }).error ?? `${res.status}`)
    }
    return res.json() as Promise<{ photo: PhotoRow; candidate: CandidatePhoto }>
  },

  async getPhoto(id: string) {
    const res = await curatorFetch(`/photos/${id}`)
    if (!res.ok) throw new Error(`${res.status}`)
    return res.json() as Promise<PhotoRow>
  },

  async listPhotos(status = 'candidate') {
    const res = await curatorFetch(`/photos?status=${status}`)
    if (!res.ok) throw new Error(`${res.status}`)
    return res.json() as Promise<{ photos: Partial<PhotoRow>[] }>
  },

  async updatePhoto(id: string, payload: VerifyPayload) {
    const res = await curatorFetch(`/photos/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error((data as { error?: string }).error ?? `${res.status}`)
    }
    return res.json() as Promise<PhotoRow>
  },

  async rejectPhoto(id: string) {
    const res = await curatorFetch(`/photos/${id}`, { method: 'DELETE' })
    if (!res.ok) throw new Error(`${res.status}`)
    return res.json()
  },

  imageUrl(id: string) {
    return `/api/curator/image/${id}`
  },

  async translate(text: string): Promise<string> {
    const res = await curatorFetch('/translate', {
      method: 'POST',
      body: JSON.stringify({ text }),
    })
    const data = (await res.json()) as { translated?: string; error?: string }
    if (!res.ok || !data.translated) {
      throw new Error(data.error ?? 'translation failed')
    }
    return data.translated
  },
}

// Frontend-side mirror of the backend types. Keep in sync with dev/curator/types.ts.

export type Era =
  | 'khedival' | 'british' | 'monarchy' | 'nasser' | 'sadat' | 'mubarak' | 'post_2011'

export type Category =
  | 'street' | 'political' | 'figure' | 'cinema' | 'architecture' | 'military' | 'sports'

export type GeoGranularity = 'building' | 'neighborhood' | 'city' | 'governorate'

export type PhotoRow = {
  id: string
  status: string
  r2_key: string
  source_url: string
  source_archive: string
  source_id: string | null
  width: number | null
  height: number | null
  phash: string
  license: string
  license_url: string | null
  attribution_text: string | null
  year: number
  year_min: number
  year_max: number
  era: Era
  lat: number
  lng: number
  geo_granularity: GeoGranularity
  place_name_en: string
  place_name_ar: string
  place_name_historical: string | null
  category: Category
  subjects: string | null
  event_anchor: string | null
  caption_en: string
  caption_ar: string
  trivia_en: string | null
  trivia_ar: string | null
  v1_provenance: 0 | 1
  v2_license: 0 | 1
  v3_year_primary: 0 | 1
  v4_year_second_src: 0 | 1
  v5_location: 0 | 1
  v6_no_conflict: 0 | 1
  verification_notes: string | null
  nostalgia_score: number | null
  created_at: number
  verified_at: number | null
  curator: string | null
}

export type CandidatePhoto = {
  source_archive: string
  source_url: string
  source_id: string
  image_url: string
  width?: number
  height?: number
  license?: string
  license_url?: string
  attribution_text?: string
  year?: number
  year_min?: number
  year_max?: number
  lat?: number
  lng?: number
  place_hint?: string
  caption_raw?: string
  creator?: string
  raw: unknown
}

export type VerifyPayload = {
  year: number
  year_min: number
  year_max: number
  era: Era
  lat: number
  lng: number
  geo_granularity: GeoGranularity
  place_name_en: string
  place_name_ar: string
  place_name_historical?: string
  category: Category
  subjects?: string[]
  event_anchor?: string
  caption_en: string
  caption_ar: string
  trivia_en?: string
  trivia_ar?: string
  license: string
  license_url?: string
  attribution_text: string
  v1_provenance: boolean
  v2_license: boolean
  v3_year_primary: boolean
  v4_year_second_src: boolean
  v5_location: boolean
  v6_no_conflict: boolean
  verification_notes: Record<string, string>
  nostalgia_score: number
  curator: string
}
